import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Calendar, TrendingUp, ShieldAlert, Repeat, FileText, Box, X, ChevronLeft, ChevronRight, Snowflake } from "lucide-react";

/**
 * Weekly Digest — last completed Mon–Sun summary for SA/Plant Head.
 * Modes:
 *  - card  : compact dashboard card (default)
 *  - modal : full-screen overlay (Monday auto-popup)
 */
export default function WeeklyDigest({ mode = "card", onClose }) {
  const { user } = useAuth();
  const isAdmin = !!user?.role?.is_super_admin || (user?.role?.name === "Plant Head/Admin");
  const [data, setData] = useState(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);

  const fmtINR = (n) => "₹" + (Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

  const load = useCallback(async (off = 0) => {
    setLoading(true);
    try {
      const r = await api.get(`/digest/weekly?week_offset=${off}`);
      setData(r.data);
    } catch (e) {
      // 400 = before FY start. Snap back to FY-Week-1.
      if (e?.response?.status === 400) {
        setOffset((cur) => Math.max(0, cur - 1));
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => { if (isAdmin) load(offset); }, [isAdmin, offset, load]);

  if (!isAdmin || !data) return null;

  const { period, grns, challans, fifo_overrides, quality, material_requests, top_materials, slowest_materials } = data;

  const Body = (
    <div className="space-y-5" data-testid="weekly-digest-body">
      {/* Period header */}
      <div className="flex items-center justify-between border-b border-[#EEEEEE] pb-3">
        <div className="flex items-center gap-3">
          <div className="bg-[#0D2B5E]/8 p-2 rounded-sm"><Calendar size={18} className="text-[#0D2B5E]"/></div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#0D2B5E]/60">Weekly Digest</div>
            <div className="text-base font-bold text-[#0D2B5E] font-mono" data-testid="digest-period-label">{period.label} · {period.start.slice(0, 10)} → {period.end.slice(0, 10)}</div>
          </div>
        </div>
      <div className="flex items-center gap-2">
        <button onClick={() => setOffset(offset + 1)} disabled={!period.has_previous} className="text-[#1565C0] hover:bg-[#F5F5F5] p-1.5 rounded-sm disabled:opacity-30 disabled:hover:bg-transparent" title={period.has_previous ? "Previous week" : `No data before ${period.fy_start || "FY start"}`} data-testid="digest-prev-week"><ChevronLeft size={16}/></button>
        <button onClick={() => setOffset(Math.max(0, offset - 1))} disabled={offset === 0} className="text-[#1565C0] hover:bg-[#F5F5F5] p-1.5 rounded-sm disabled:opacity-30" title="Next week" data-testid="digest-next-week"><ChevronRight size={16}/></button>
          {mode === "modal" && (
            <button onClick={onClose} className="text-[#0D2B5E]/60 hover:text-[#C62828] p-1.5 ml-2 rounded-sm" data-testid="digest-close-btn"><X size={18}/></button>
          )}
        </div>
      </div>

      {/* KPI grid — Wave-21: Logins tile removed per user request */}
      <div className="grid grid-cols-3 gap-3">
        <Tile icon={Box} label="GRNs received" primary={grns.count} secondary={fmtINR(grns.value)} color="#1565C0" testId="digest-grns"/>
        <Tile icon={FileText} label="Delivery Challans" primary={challans.count} secondary={`${fmtINR(challans.value)} · ${challans.returnable_open} returnable open`} color="#2E7D32" testId="digest-challans"/>
        <Tile icon={Repeat} label="FIFO overrides" primary={fifo_overrides.raised} secondary={`${fifo_overrides.approved} approved · ${fifo_overrides.rejected} rejected`} color="#E65100" testId="digest-fifo"/>
        <Tile icon={ShieldAlert} label="Quality rejections" primary={quality.rejections} secondary={(quality.top_reasons[0]?.name) || "no NG events"} color="#C62828" testId="digest-quality"/>
        <Tile icon={TrendingUp} label="Material Requests" primary={material_requests.raised} secondary={`${material_requests.approved} approved · ${material_requests.rejected} rejected`} color="#1565C0" testId="digest-mr"/>
      </div>

      {/* Top materials + top NG reasons side by side */}
      <div className="grid grid-cols-2 gap-3">
        <div className="border border-[#EEEEEE] rounded-sm" data-testid="digest-top-materials">
          <div className="px-4 py-2 bg-[#F5F5F5] text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/70">Top 5 issued materials</div>
          {top_materials.length === 0
            ? <div className="p-6 text-center text-sm text-[#0D2B5E]/40 italic">no issues this week</div>
            : <table className="w-full text-sm">
                <tbody>{top_materials.map((m, i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"}>
                    <td className="px-4 py-2 font-mono text-[#1565C0] text-xs">{m.code}</td>
                    <td className="px-4 py-2 text-xs">{m.name}</td>
                    <td className="px-4 py-2 font-mono text-right text-[#0D2B5E]">{m.qty}</td>
                  </tr>
                ))}</tbody>
              </table>}
        </div>
        <div className="border border-[#EEEEEE] rounded-sm" data-testid="digest-top-reasons">
          <div className="px-4 py-2 bg-[#F5F5F5] text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/70">Top quality NG reasons</div>
          {quality.top_reasons.length === 0
            ? <div className="p-6 text-center text-sm text-[#2E7D32] italic">No NG events ✓</div>
            : <table className="w-full text-sm">
                <tbody>{quality.top_reasons.map((r, i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"}>
                    <td className="px-4 py-2 text-xs">{r.name}</td>
                    <td className="px-4 py-2 font-mono text-right text-[#C62828] font-bold">{r.count}</td>
                  </tr>
                ))}</tbody>
              </table>}
        </div>
      </div>

      {loading && <div className="text-center text-xs text-[#0D2B5E]/40">Loading…</div>}

      {/* Slowest-moving materials — dead-stock signal */}
      <div className="border border-[#FFB74D]/40 rounded-sm bg-[#FFB74D]/5" data-testid="digest-slowest-materials">
        <div className="px-4 py-2 bg-[#FFB74D]/15 text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/80 flex items-center gap-2">
          <Snowflake size={12} className="text-[#E65100]"/>
          Slowest-moving materials · &gt;60 days in stock, no outward in 30 days
          <span className="ml-auto text-[#0D2B5E]/50 font-normal normal-case tracking-normal text-[10px]">ranked by ₹ value tied up</span>
        </div>
        {(!slowest_materials || slowest_materials.length === 0)
          ? <div className="p-6 text-center text-sm text-[#2E7D32] italic">No dead stock — every SKU has moved recently ✓</div>
          : <table className="w-full text-sm">
              <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
                <tr>
                  <th className="text-left px-4 py-2">Code</th>
                  <th className="text-left px-4 py-2">Material</th>
                  <th className="text-right px-4 py-2">Qty in stock</th>
                  <th className="text-right px-4 py-2">Oldest age</th>
                  <th className="text-right px-4 py-2">₹ Value tied up</th>
                </tr>
              </thead>
              <tbody>{slowest_materials.map((m, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"} data-testid={`slow-row-${i}`}>
                  <td className="px-4 py-2 font-mono text-[#1565C0] text-xs">{m.code}</td>
                  <td className="px-4 py-2 text-xs">{m.name}</td>
                  <td className="px-4 py-2 font-mono text-right">{m.qty}</td>
                  <td className={`px-4 py-2 font-mono text-right ${m.age_days > 120 ? "text-[#C62828] font-bold" : m.age_days > 90 ? "text-[#E65100]" : "text-[#0D2B5E]/70"}`}>{m.age_days != null ? `${m.age_days}d` : "—"}</td>
                  <td className="px-4 py-2 font-mono text-right text-[#0D2B5E] font-bold">{fmtINR(m.value)}</td>
                </tr>
              ))}</tbody>
            </table>}
      </div>
    </div>
  );

  if (mode === "modal") {
    return (
      <div className="fixed inset-0 z-50 bg-[#0D2B5E]/40 backdrop-blur-sm flex items-start justify-center p-8 overflow-y-auto" data-testid="digest-modal">
        <div className="bg-white border border-[#EEEEEE] rounded-sm shadow-2xl max-w-4xl w-full p-6">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#FFB74D] mb-2">Monday Briefing</div>
          {Body}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#EEEEEE] rounded-sm p-5" data-testid="weekly-digest-card">
      {Body}
    </div>
  );
}

function Tile({ icon: Icon, label, primary, secondary, color, testId }) {
  return (
    <div className="border border-[#EEEEEE] rounded-sm p-4 bg-white" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0D2B5E]/60">{label}</div>
          <div className="text-2xl font-bold font-mono mt-1" style={{ color }}>{primary}</div>
          <div className="text-[11px] text-[#0D2B5E]/60 mt-0.5 truncate" title={secondary}>{secondary}</div>
        </div>
        <div className="p-1.5 rounded-sm" style={{ backgroundColor: `${color}1A`, color }}>
          <Icon size={14} />
        </div>
      </div>
    </div>
  );
}
